import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../parts/modal-new";

const UNDERLOG_STEP = 2;

const tt = (t, key, opts) => t(`nutritionPage.progress.hero.adjustModal.${key}`, opts);

// Single source of truth for what "manual" prefills to — used both to seed
// the input on open and as the dirty-check baseline below, so the two can't
// drift out of sync the way 2 separately-written copies of this fallback
// chain could. estExpenditure is only ever set when expenditure.confident
// (buildEnergyExpenditureStats' own contract), so this needs no separate
// canCalc branch — it already falls through correctly when !canCalc.
const resolveManualValue = (expenditure) =>
  expenditure.manualValue ?? expenditure.workingExpenditure ?? expenditure.estExpenditure ?? null;

export const TdeeAdjustModal = ({ isOpen, onClose, expenditure, onSave, onReset }) => {
  const { t } = useTranslation("athlete-portal");
  // canCalc = there's an estExpenditure to nudge — "calc" mode has nothing
  // to multiply against otherwise (see buildEnergyExpenditureStats' own
  // comment on the !confident branch). When false, this modal is reached
  // via NeedMoreData's "Set TDEE manually" CTA, not TdeeCard's "Adjust".
  const canCalc = expenditure.confident;

  const [mode, setMode] = useState("manual");
  const [underLogPct, setUnderLogPct] = useState(0);
  const [manualValue, setManualValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Reset every time the modal opens — prefilled from the current
  // expenditure/override, forced to "manual" when calc isn't available.
  useEffect(() => {
    if (!isOpen) return;
    setMode(canCalc ? expenditure.overrideMode : "manual");
    setUnderLogPct(expenditure.underLogPct ?? 0);
    setManualValue(String(resolveManualValue(expenditure) ?? ""));
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const parsedManual = Number.parseFloat(manualValue);
  const isValid = mode === "calc" || (!Number.isNaN(parsedManual) && parsedManual > 0);
  // Save stays disabled until something actually differs from what the
  // modal opened with — same prefill expressions as the useEffect above,
  // recomputed each render instead of stored, since expenditure/canCalc
  // don't change while the modal is open.
  const originalMode = canCalc ? expenditure.overrideMode : "manual";
  const originalUnderLogPct = expenditure.underLogPct ?? 0;
  const originalManualValue = resolveManualValue(expenditure);
  const hasChanges =
    mode !== originalMode ||
    (mode === "calc" && underLogPct !== originalUnderLogPct) ||
    (mode === "manual" && parsedManual !== originalManualValue);
  const canSave = isValid && hasChanges;
  // Only meaningful in calc mode, where estExpenditure exists (canCalc).
  const addedKcal = canCalc ? Math.round(expenditure.estExpenditure * (underLogPct / 100)) : 0;
  const showReset = canCalc && expenditure.hasOverride;

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({
        mode,
        underLogPct,
        manualValue: mode === "manual" ? parsedManual : null,
      });
      onClose();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (isResetting) return;
    if (!window.confirm(tt(t, "confirmReset"))) return;
    setIsResetting(true);
    setSaveError(null);
    try {
      await onReset();
      onClose();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Modal
      title={tt(t, "title")}
      className="np-tdee-modal"
      onClose={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="body np-tdee-modal__body">
        {canCalc ? (
          // Segmented control, not 2 freestanding data-button-type buttons —
          // this is a single either/or choice (unlike SuggestedIntakeCard's
          // 5 independent rate buttons, which this used to copy visually).
          // One shared pill track communicates "one control, two states"
          // instead of reading as "an important button + a disabled one".
          <div className="np-tdee-modal__mode-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "calc"}
              className={`np-tdee-modal__mode-btn${mode === "calc" ? " np-tdee-modal__mode-btn--active" : ""}`}
              onClick={() => setMode("calc")}
            >
              {tt(t, "modeFromData")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "manual"}
              className={`np-tdee-modal__mode-btn${mode === "manual" ? " np-tdee-modal__mode-btn--active" : ""}`}
              onClick={() => setMode("manual")}
            >
              {tt(t, "modeSetManually")}
            </button>
          </div>
        ) : (
          <p className="np-tdee-modal__no-data-hint">
            {tt(t, "noDataHint")}
          </p>
        )}

        {mode === "calc" && canCalc && (
          <div className="np-tdee-modal__stepper-section">
            <span className="np-tdee-modal__field-label">{tt(t, "underLogStepperLabel")}</span>
            <div className="np-tdee-modal__stepper">
              <button
                type="button"
                data-button-type="secondary"
                onClick={() => setUnderLogPct((pct) => Math.max(0, pct - UNDERLOG_STEP))}
                disabled={underLogPct <= 0}
                aria-label={tt(t, "decreasePct")}
              >
                −
              </button>
              <span className="np-tdee-modal__stepper-value">{underLogPct}%</span>
              <button
                type="button"
                data-button-type="secondary"
                onClick={() => setUnderLogPct((pct) => pct + UNDERLOG_STEP)}
                aria-label={tt(t, "increasePct")}
              >
                +
              </button>
              <span className="np-tdee-modal__stepper-preview">
                {tt(t, "addsKcal", { value: addedKcal.toLocaleString() })}
              </span>
            </div>
          </div>
        )}

        {mode === "manual" && (
          <div className="np-tdee-modal__manual-section">
            <label htmlFor="np-tdee-manual-input">{tt(t, "manualInputLabel")}</label>
            <input
              id="np-tdee-manual-input"
              type="number"
              className="np-tdee-modal__manual-input"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
            />
          </div>
        )}

        {showReset && (
          <button
            type="button"
            data-button-type="secondary"
            className="np-hero__adjust-btn np-tdee-modal__reset-btn"
            onClick={handleReset}
            disabled={isResetting}
          >
            <span className="np-plan-bar__cta-label">{tt(t, "resetToCalculated")}</span>
          </button>
        )}
      </div>

      {saveError && (
        <p className="np-modal-footer__error" role="alert">{saveError}</p>
      )}

      <footer className="np-tdee-modal__footer">
        <button
          type="button"
          data-button-type="secondary"
          className="np-modal-footer__cancel-btn"
          onClick={onClose}
        >
          {tt(t, "cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          className="np-modal-footer__save-btn"
          onClick={handleSave}
          disabled={!canSave || isSaving}
        >
          {tt(t, "save")}
        </button>
      </footer>
    </Modal>
  );
};

TdeeAdjustModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  expenditure: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default TdeeAdjustModal;
